import type { Skill, SkillOwnerList } from "@ark-bot/protocol";
import { useCallback, useEffect, useState } from "react";

import { api } from "../api.js";
import { useText } from "../i18n.js";
import { SETTINGS } from "../text/settings.js";
import { SKILLS } from "../text/skills.js";

import { useConfirm } from "./ConfirmDialog.js";

interface Catalog {
  readonly shared: Skill[];
  readonly sharedDirectory: string;
  readonly agents: SkillOwnerList[];
}

interface Draft {
  readonly owner: "shared" | string;
  readonly id: string | null;
  readonly name: string;
  readonly description: string;
  readonly body: string;
}

const blank = (owner: "shared" | string): Draft => ({ owner, id: null, name: "", description: "", body: "" });

function toDraft(skill: Skill, owner: "shared" | string): Draft {
  return { owner, id: skill.id, name: skill.name, description: skill.description, body: skill.body };
}

/**
 * Settings → Skills: every written procedure in the workspace, shared ones
 * first (every bot follows them) then each bot's own. A row opens the editor.
 */
export function WorkspaceSkills(): React.JSX.Element {
  const t = useText(SETTINGS);
  const s = useText(SKILLS);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.allSkills();
      setCatalog(list);
      setError(null);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const confirm = useConfirm();
  const act = async (work: () => Promise<unknown>): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
      return true;
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (draft == null) return;
    const body = { name: draft.name.trim(), description: draft.description.trim(), body: draft.body.trim() };
    const shared = draft.owner === "shared";
    const saved = await act(() => {
      if (draft.id == null) return shared ? api.createSharedSkill(body) : api.createSkill(draft.owner, body);
      return shared ? api.updateSharedSkill(draft.id, body) : api.updateSkill(draft.owner, draft.id, body);
    });
    if (saved) setDraft(null);
  };

  const remove = async (skill: Skill, owner: "shared" | string): Promise<void> => {
    const yes = await confirm({ title: t.skillsDeleteTitle(skill.name), message: t.skillsDeleteMessage, action: s.delete, danger: true });
    if (!yes) return;
    const deleted = await act(() => (owner === "shared" ? api.deleteSharedSkill(skill.id) : api.deleteSkill(owner, skill.id)));
    if (deleted) setDraft(null);
  };

  if (draft != null) {
    const current =
      catalog == null
        ? null
        : draft.id == null
          ? null
          : draft.owner === "shared"
            ? (catalog.shared.find((skill) => skill.id === draft.id) ?? null)
            : (catalog.agents.find((agent) => agent.id === draft.owner)?.skills.find((skill) => skill.id === draft.id) ?? null);
    const ownerLabel = draft.owner === "shared" ? t.skillsShared : (catalog?.agents.find((agent) => agent.id === draft.owner)?.name ?? draft.owner);
    return (
      <form className="agent-settings workspace-skills" onSubmit={(event) => void save(event)}>
        <p className="muted settings-lead">{ownerLabel}</p>
        {error != null ? <div className="settings-error">{error}</div> : null}
        <label>
          {s.name}
          <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder={s.namePlaceholder} required />
        </label>
        <label>
          {s.whenToUse} <span className="muted">{s.whenToUseHint}</span>
          <input value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} placeholder={s.whenToUsePlaceholder} />
        </label>
        <label>
          {s.instructions}
          <textarea rows={8} value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value })} placeholder={s.instructionsPlaceholder} required />
        </label>
        <div className="settings-actions">
          <button type="submit" className="primary" disabled={busy}>
            {draft.id == null ? s.create : s.save}
          </button>
          <button type="button" onClick={() => setDraft(null)} disabled={busy}>
            {s.cancel}
          </button>
          {current == null ? null : (
            <button type="button" className="danger" disabled={busy} onClick={() => void remove(current, draft.owner)}>
              {s.delete}
            </button>
          )}
        </div>
      </form>
    );
  }

  const empty = catalog != null && catalog.shared.length === 0 && catalog.agents.every((agent) => agent.skills.length === 0);

  return (
    <div className="workspace-skills">
      <p className="muted settings-lead">{t.skillsLead}</p>
      {error != null ? <div className="settings-error">{error}</div> : null}
      {catalog == null ? (
        <p className="muted">{s.loading}</p>
      ) : empty ? (
        <p className="muted">{t.skillsNone}</p>
      ) : (
        <>
          <Group title={t.skillsShared} note={`${t.skillsSharedAbout} `} directory={catalog.sharedDirectory} skills={catalog.shared} empty={t.skillsEmptyShared} onOpen={(skill) => setDraft(toDraft(skill, "shared"))} noDescription={s.noDescription} openLabel={t.skillsOpen} />
          {catalog.agents.map((agent) =>
            agent.skills.length === 0 ? null : (
              <Group key={agent.id} title={agent.name} skills={agent.skills} empty="" onOpen={(skill) => setDraft(toDraft(skill, agent.id))} noDescription={s.noDescription} openLabel={t.skillsOpen} />
            ),
          )}
        </>
      )}
      {catalog == null ? null : (
        <div className="settings-actions">
          <button type="button" onClick={() => setDraft(blank("shared"))} disabled={busy}>
            {t.skillsNewShared}
          </button>
        </div>
      )}
    </div>
  );
}

function Group({
  title,
  note,
  directory,
  skills,
  empty,
  onOpen,
  noDescription,
  openLabel,
}: {
  readonly title: string;
  readonly note?: string;
  readonly directory?: string;
  readonly skills: readonly Skill[];
  readonly empty: string;
  readonly onOpen: (skill: Skill) => void;
  readonly noDescription: string;
  readonly openLabel: (name: string) => string;
}): React.JSX.Element {
  return (
    <section className="settings-section">
      <h4>{title}</h4>
      {note == null ? null : (
        <p className="muted settings-note">
          {note}
          {directory == null ? null : <code>{directory}</code>}
        </p>
      )}
      {skills.length === 0 ? (
        <p className="muted">{empty}</p>
      ) : (
        <ul className="summary-list">
          {skills.map((skill) => (
            <li key={skill.id} className="has-open">
              <button type="button" className="summary-open" onClick={() => onOpen(skill)} aria-label={openLabel(skill.name)}>
                <strong>{skill.name}</strong>
                <span className="muted">{skill.description || noDescription}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
